import {
  Accordion,
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { NotificationTypeDefinition } from '@polarissleep/shared';
import { ALL_NOTIFICATIONS } from '@polarissleep/shared';
import { IconAlertCircle, IconArrowLeft, IconDeviceFloppy } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

const CHANNEL_BADGE_COLORS: Record<string, string> = {
  'email-body': 'blue',
  'email-subject': 'cyan',
  'sms-body': 'green',
  'in-app-body': 'orange',
};

/**
 * Available template variables by context generator.
 * These are the variables admins can use in Pug templates.
 */
const COMMON_VARIABLES = [{ name: 'firstName', description: "Recipient's first name" }];

const CONTEXT_VARIABLES: Record<string, { name: string; description: string }[]> = {
  taskAssignment: [
    ...COMMON_VARIABLES,
    { name: 'taskTitle', description: 'Title of the assigned task' },
    { name: 'taskDescription', description: 'Task description' },
    { name: 'taskIsUrgent', description: 'Whether the task is urgent (boolean)' },
    { name: 'taskLink', description: 'Link to the task' },
    { name: 'requesterName', description: 'Name of the person who requested the task' },
  ],
  taskDueSoon: [
    ...COMMON_VARIABLES,
    { name: 'taskTitle', description: 'Title of the task' },
    { name: 'taskDescription', description: 'Task description' },
    { name: 'taskIsUrgent', description: 'Whether the task is urgent (boolean)' },
    { name: 'taskLink', description: 'Link to the task' },
    { name: 'dueDate', description: 'Due date of the task' },
  ],
  inboxMessage: [
    ...COMMON_VARIABLES,
    { name: 'senderName', description: 'Full name of the message sender' },
    { name: 'senderFirstName', description: 'First name of the sender' },
    { name: 'messageContent', description: 'Content of the message' },
    { name: 'messageTopic', description: 'Topic/subject of the message thread' },
  ],
};

export function NotificationTemplateEditor() {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [docRef, setDocRef] = useState<DocumentReference | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const fetchTemplate = useCallback(async () => {
    if (!templateId) {
      return;
    }
    setLoading(true);
    try {
      const resource = await medplum.readResource('DocumentReference', templateId);
      setDocRef(resource);

      const data = resource.content?.[0]?.attachment?.data;
      if (data) {
        setContent(atob(data));
      }
    } catch (error) {
      console.error('Failed to fetch template:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load template',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [medplum, templateId]);

  useEffect(() => {
    fetchTemplate().catch(console.error);
  }, [fetchTemplate]);

  const handleSave = async () => {
    if (!docRef || !templateId) {
      return;
    }
    setSaving(true);
    try {
      const updated: DocumentReference = {
        ...docRef,
        content: [
          {
            attachment: {
              contentType: 'text/plain',
              data: btoa(content),
              title: docRef.content?.[0]?.attachment?.title ?? templateId,
            },
          },
        ],
      };

      const result = await medplum.updateResource(updated);
      setDocRef(result);
      setIsDirty(false);
      notifications.show({
        title: 'Saved',
        message: 'Template updated successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to save template:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save template',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const getTemplateKey = (): string => {
    return docRef?.type?.coding?.[0]?.code ?? docRef?.type?.text ?? 'unknown';
  };

  const getChannel = (): string => {
    return docRef?.category?.[0]?.coding?.[0]?.code ?? 'unknown';
  };

  const getNotificationDef = (): NotificationTypeDefinition | null => {
    const description = docRef?.description ?? '';
    for (const def of Object.values(ALL_NOTIFICATIONS)) {
      if (description.includes(def.key)) {
        return def;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <Paper p="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Paper>
    );
  }

  if (!docRef) {
    return (
      <Paper p="xl">
        <Text>Template not found</Text>
      </Paper>
    );
  }

  const notifDef = getNotificationDef();
  const channel = getChannel();

  return (
    <Paper p="md">
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/admin/notification-templates')}
          >
            Back to Templates
          </Button>
        </Group>

        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Title order={2}>Edit Template</Title>
            <Group gap="xs">
              <Code>{getTemplateKey()}</Code>
              <Badge size="sm" color={CHANNEL_BADGE_COLORS[channel] ?? 'gray'} variant="light">
                {channel}
              </Badge>
            </Group>
            {notifDef && (
              <Text size="sm" c="dimmed">
                {notifDef.label} — {notifDef.description}
              </Text>
            )}
          </Stack>

          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={!isDirty}
          >
            Save
          </Button>
        </Group>

        <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light" title="Template Syntax">
          Templates use Pug syntax. Variables are referenced with <Code>{'#{variableName}'}</Code>. Changes take effect
          on the next notification sent.
        </Alert>

        <Accordion variant="contained">
          <Accordion.Item value="variables">
            <Accordion.Control>Available Template Variables</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {Object.entries(CONTEXT_VARIABLES).map(([contextName, vars]) => (
                  <div key={contextName}>
                    <Text fw={600} size="sm" mb="xs">
                      {contextName}
                    </Text>
                    {vars.map((v) => (
                      <Group key={v.name} gap="xs" mb={4}>
                        <Code>{`#{${v.name}}`}</Code>
                        <Text size="xs" c="dimmed">
                          {v.description}
                        </Text>
                      </Group>
                    ))}
                  </div>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <Textarea
          label="Template Content (Pug)"
          value={content}
          onChange={(e) => {
            setContent(e.currentTarget.value);
            setIsDirty(true);
          }}
          minRows={15}
          maxRows={30}
          autosize
          styles={{
            input: {
              fontFamily: 'monospace',
              fontSize: '13px',
            },
          }}
        />
      </Stack>
    </Paper>
  );
}
